import React, { useRef, useState, useEffect } from "react";
import approveSeal from "../images/approve.jpg";
import { Button, Empty, Table, Spin, DatePicker } from 'antd';
import axios from "../../services/axios";
import { useNavigate } from "react-router-dom";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import { CONSULTANT_DOCUMENT_CONTROLLER, DOCUMENT_CONTROLLER } from "../../constants/role";

const ViewTransmittal = () => {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [assignedProjects, setAssignedProjects] = useState([]);
  const role = JSON.parse(localStorage.getItem("role"));
  const user = localStorage.getItem("user");
  const projectName = JSON.parse(localStorage.getItem('Project'));
  const userType = localStorage.getItem('userType');
  const [showForm, setShowForm] = useState(false);
  const [transmittalForms, setTransmittalForms] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false)

  const hideEngineerRemarks =
    role.includes(DOCUMENT_CONTROLLER) && userType === "Contractor";

  const hideClientRemarks =
    (role.includes(DOCUMENT_CONTROLLER) && userType === "Contractor") ||
    (role.includes(CONSULTANT_DOCUMENT_CONTROLLER) && userType === "Consultant");

  const hideContractorRemarks =
    role.includes(CONSULTANT_DOCUMENT_CONTROLLER) && userType === "Consultant";

  const [formData, setFormData] = useState({
    transmittalId: "",
    projectName: "",
    contractForm: "",
    date: "",
    contractor: "",
    engineer: "",
    client: "",
    revisionNo: "",
    from: "",
    to: "",
    documents: [],
    contractorText: "",
    engineerName: "",
    engineerDate: "",
    clientName: "",
    clientDate: "",
    clientText: "",
    approvedBy: "",
    approvedDate: "",
    Materials: { checked: false },
    Drawings: { checked: false },
    Schedules: { checked: false },
    Commercial: { checked: false },
    Correspondence: { checked: false },
    Reports: { checked: false },
    Documents: { checked: false },
    ForApproval: false,
    ForYourInformation: false,
    ResponseRequired: false,
    Approved: false,
    ApprovedWithComments: false,
    ReviseAndResubmit: false,
    Rejected: false,
    Responded: false,
    Original: false,
    Copies: false,
    Contractor: false,
    responseDate: ""
  });

  const normalizeDate = (date) => {
    if (!date) return "";
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate)) return "";
      return parsedDate.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const getUniqueFilters = (key) => {
    let values;
    if (key === "date" || key === "responseDate") {
      values = transmittalForms
        .map((form) => (form[key] ? new Date(form[key]).toISOString().split("T")[0] : null))
        .filter(Boolean);
    } else {
      values = transmittalForms.map((form) => form[key]).filter(Boolean);
    }
    return [...new Set(values)]
      .sort()
      .map((value) => ({ text: value, value }));
  };

  const getDocRefFilters = () => {
    const refs = [];
    transmittalForms.forEach((row) => {
      row.documents?.forEach((doc) => {
        if (doc.docRef) refs.push(doc.docRef);
      });
    });

    return [...new Set(refs)].map((ref) => ({
      text: ref,
      value: ref,
    }));
  };

  const getDocDescFilters = () => {
    const descs = [];
    transmittalForms.forEach((row) => {
      row.documents?.forEach((doc) => {
        if (doc.description) descs.push(doc.description);
      });
    });

    return [...new Set(descs)].map((desc) => ({
      text: desc,
      value: desc,
    }));
  };

  const getStatusLabel = (record) => {
    if (record.Approved) return "Approved";
    if (record.ApprovedWithComments) return "Approved with Comments";
    if (record.ReviseAndResubmit) return "Revise & Resubmit";
    if (record.Rejected) return "Rejected";
    if (record.Responded) return "Responded";

    if (record.ResponseRequired) return "Response Required";
    if (record.ForApproval) return "For Approval";
    if (record.ForYourInformation) return "For Your Information";

    return "NA";
  };

  const fetchProjectsAndForms = async () => {
    setLoading(true)
    try {
      const projectResponse = await axios.get("/get-projects", { params: { user } });

      if (projectResponse.data.projects.length > 0) {
        setAssignedProjects(projectResponse.data.projects);

        if (!projectName) {
          setTransmittalForms([]);
          return;
        }

        const transmittalResponse = await axios.get("/all-Transmittal", {
          params: { projects: [projectName] },
        });

        if (transmittalResponse.data.length > 0) {
          setTransmittalForms((prevForms) => {
            const newForms = transmittalResponse.data.filter(
              (newForm) => !prevForms.some((existingForm) => existingForm.transmittalId === newForm.transmittalId)
            );
            return [...prevForms, ...newForms];
          });
        } else {
          setTransmittalForms([]);
        }
      } else {
        setAssignedProjects([]);
        setTransmittalForms([]);
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error("Error fetching projects or transmittal forms:", error);
      setTransmittalForms([]);
    }
  };

  useEffect(() => {
    fetchProjectsAndForms();
  }, [user, projectName]);

  const columns = [
    ...(role && role.includes("Super User")
      ? [
          {
            title: "Project Name",
            dataIndex: "projectName",
            key: "projectName",
            width: 250,
            render: (text) => (
              <div className="break-words p-2 max-h-[60px] overflow-auto">
                {text || "Select a Project"}
              </div>
            ),
          },
        ]
      : []),

    {
      title: "Transmittal ID",
      dataIndex: "transmittalId",
      key: "transmittalId",
      width: 200,
      render: (text) => (
        <div className="p-2 font-mono text-[15px] break-words whitespace-normal">
          {text}
        </div>
      ),
    },

    {
      title: "Transmittal Date",
      dataIndex: "date",
      key: "date",
      width: 130,
      filters: getUniqueFilters("date"),
      filterSearch: true,
      onFilter: (value, record) =>
        normalizeDate(record.date) === value,
      render: (date) => (
        <div className="p-2 text-center">
          {date ? new Date(date).toISOString().split("T")[0] : "-"}
        </div>
      ),
    },

    // ✅ SIMPLE DOCUMENT REFERENCES (NO FILTERS)
    {
      title: "Document References",
      key: "docRefs",
      width: 200,
      filters: getDocRefFilters(),
      filterSearch: true,
      onFilter: (value, record) =>
        record.documents?.some(
          (doc) =>
            doc.docRef &&
            doc.docRef.toLowerCase().includes(value.toLowerCase())
        ),
      render: (_, record) => (
        <span className="block p-2 break-words">
          {record.documents?.length
            ? record.documents.map((d) => d.docRef).join(", ")
            : "NA"}
        </span>
      ),
    },

    // ✅ SIMPLE DOCUMENT DESCRIPTIONS (NO FILTERS)
    {
      title: "Document Descriptions",
      key: "docDescriptions",
      width: 260,
      filters: getDocDescFilters(),
      filterSearch: true,
      onFilter: (value, record) =>
        record.documents?.some(
          (doc) =>
            doc.description &&
            doc.description.toLowerCase().includes(value.toLowerCase())
        ),
      render: (_, record) => (
        <span className="block p-2 break-words text-gray-700">
          {record.documents?.length
            ? record.documents.map((d) => d.description).join(", ")
            : "NA"}
        </span>
      ),
    },

    {
      title: "Revision",
      dataIndex: "revisionNo",
      key: "revisionNo",
      width: 100,
      render: (text) => (
        <div className="p-2 text-center font-medium">
          {text || "NA"}
        </div>
      ),
    },

    {
      title: "Status",
      key: "status",
      width: 180,
      render: (_, record) => (
        <div className="p-2 text-center">
          {getStatusLabel(record)}
        </div>
      ),
    },

    {
      title: "From",
      dataIndex: "from",
      key: "from",
      width: 120,
      render: (text) => (
        <div className="p-2 break-words">
          {text || "NA"}
        </div>
      ),
    },

    {
      title: "To",
      dataIndex: "to",
      key: "to",
      width: 120,
      render: (text) => (
        <div className="p-2 break-words">
          {text || "NA"}
        </div>
      ),
    },

    {
      title: "Actions",
      key: "action",
      width: 140,
      fixed: "right",
      render: (form) => (
        <div className="flex justify-center gap-2">
          <Button
            icon={<EyeOutlined />}
            size="small"
            disabled={disabled}
            title="View Transmittal"
            onClick={() => {
              setShowForm(true);
              setDisabled(true);
              setFormData({
                ...form,
                documents: form.documents || [],
                Materials: form.Materials || { checked: false },
                Drawings: form.Drawings || { checked: false },
                Schedules: form.Schedules || { checked: false },
                Commercial: form.Commercial || { checked: false },
                Correspondence: form.Correspondence || { checked: false },
                Reports: form.Reports || { checked: false },
                Documents: form.Documents || { checked: false },
                ForApproval: form.ForApproval || false,
                ForYourInformation: form.ForYourInformation || false,
                ResponseRequired: form.ResponseRequired || false,
                Approved: form.Approved || false,
                ApprovedWithComments: form.ApprovedWithComments || false,
                ReviseAndResubmit: form.ReviseAndResubmit || false,
                Rejected: form.Rejected || false,
                Responded: form.Responded || false,
                Original: form.Original || false,
                Copies: form.Copies || false,
                Contractor: form.Contractor || false,
                responseDate: form.responseDate || "",
              });
            }}
          />
        </div>
      ),
    },
  ];

  const handlePrint = () => {
    const originalContent = document.body.innerHTML;
    const printContent = formRef.current.cloneNode(true);

    const checkboxes = formRef.current.querySelectorAll("input[type='checkbox']");
    const clonedCheckboxes = printContent.querySelectorAll("input[type='checkbox']");

    checkboxes.forEach((checkbox, index) => {
      if (checkbox.checked) {
        clonedCheckboxes[index].setAttribute("checked", "checked");
      }
    });

    const style = `
      <style>
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-content {
            width: 100%;
            max-height: 100vh;
            overflow: hidden;
            page-break-inside: avoid;
            transform: scale(0.95);
            transform-origin: top;
            padding-top: 15mm;
          }
          button, .no-print {
            display: none !important;
          }
        }
      </style>
    `;

    const printWrapper = document.createElement("div");
    printWrapper.className = "print-content";
    printWrapper.appendChild(printContent);

    document.body.innerHTML = style + printWrapper.outerHTML;

    window.print();

    setTimeout(() => {
      document.body.innerHTML = originalContent;
      window.location.reload();
    }, 100);
  };

  return (
    <div className="relative flex-1 p-5 overflow-y-auto ml-5 mr-2 min-h-screen">
      {!showForm ? (
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spin tip="Loading..." size="large" />
            </div>
          ) : (
            <>
          <div className="flex justify-between items-center mb-4">
            <Button
              type="primary"
              onClick={() => navigate('/home/transmittal-template')}
            >
              + New Transmittal
            </Button>
          </div>

          {!projectName ? (
            <Empty description="No Project Selected. Please select a project." className="text-center mt-4" />
          ) : transmittalForms.length > 0 ? (
            <Table
              dataSource={transmittalForms}
              columns={columns}
              rowKey="transmittalId"
              bordered
              pagination={{ pageSize: 10 }}
              className="shadow-md"
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '1.5'
              }}
              scroll={{ x: 1200 }}
            />
          ) : (
            <Empty description={`No Transmittal Data Available for ${projectName}`} className="text-center mt-4" />
            )}
          </>
          )}
        </div>
      ) : (
        <div className="mx-auto bg-white p-6 shadow-lg rounded-xl">
          <Button
            type="default"
            onClick={() => {
              setShowForm(false);
              setDisabled(false);
            }}
            className="mb-4"
          >
            Back
          </Button>
          <form ref={formRef}>
            <div className="flex justify-between items-center mb-2 gap-2">
              <div className="flex gap-2 flex-1">
                <div className="w-1/5 h-16 flex items-center justify-center p-1 border rounded text-sm">
                  {formData.client || "Client"}
                </div>
                <div className="w-1/5 h-16 flex items-center justify-center p-1 border rounded text-sm">{formData.contractor || "Contractor"}</div>
                <div className="w-1/5 h-16 flex items-center justify-center p-1 border rounded text-sm">{formData.engineer || "Engineer"}</div>
                <div className="w-2/5 h-16 flex items-center justify-center p-1 border rounded font-bold text-lg">Document Transmittal Sheet</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="flex items-center">
                <label className="w-20 font-semibold">Project:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="projectName"
                  value={formData.projectName}
                  disabled
                />
              </div>
              {formData.transmittalId ? (
                <div className="flex items-center">
                  <label className="w-20 font-semibold">Transmittal ID:</label>
                  <span className="flex-1 border p-1 h-6 bg-gray-200">{formData.transmittalId}</span>
                </div>
              ) : (
                <p>Loading Transmittal ID...</p>
              )}
              <div className="flex items-center">
                <label className="w-20 font-semibold">Contract No:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="contractForm"
                  value={formData.contractForm}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="w-20 font-semibold">Date:</label>
                <input
                  type="date"
                  className="flex-1 border p-1 h-6"
                  value={normalizeDate(formData.date)}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="w-20 font-semibold">Contractor:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="contractor"
                  value={formData.contractor}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="w-20 font-semibold">Revision No:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="revisionNo"
                  value={formData.revisionNo}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="w-20 font-semibold">From:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="from"
                  value={formData.from}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="w-20 font-semibold">To:</label>
                <input
                  type="text"
                  className="flex-1 border p-1 h-6"
                  name="to"
                  value={formData.to}
                  disabled
                />
              </div>
            </div>

            <div className="border p-4 mb-2 text-xs">
              <div className="flex gap-4">
                <div className="w-2/4">
                  <p><strong>We transmit herewith:</strong></p>
                  <div className="border p-2 flex justify-between">
                    <div className="w-1/2 pr-2">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Materials?.checked || false}
                          disabled
                        />
                        <span>Materials</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Drawings?.checked || false}
                          disabled
                        />
                        <span>Drawings</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Schedules?.checked || false}
                          disabled
                        />
                        <span>Schedules</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Commercial?.checked || false}
                          disabled
                        />
                        <span>Commercial</span>
                      </div>
                    </div>
                    <div className="w-1/2 pl-2">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Correspondence?.checked || false}
                          disabled
                        />
                        <span>Correspondence</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Reports?.checked || false}
                          disabled
                        />
                        <span>Reports</span>
                      </div>
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={formData.Documents?.checked || false}
                          disabled
                        />
                        <span>Documents</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-1/4">
                  <div className="font-semibold mb-1">The following items:</div>
                  <div className="border p-1 text-xs">
                    <div className="font-semibold text-gray-500">Action Codes:</div>
                    <div>A: Approved</div>
                    <div>B: Approved with Comments</div>
                    <div>C: Revise & Resubmit</div>
                    <div>D: Rejected</div>
                  </div>
                </div>
              </div>
            </div>

            <table className="w-full border text-xs mb-2">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-1">SL.NO</th>
                  <th className="border p-1">Doc.Reference</th>
                  <th className="border p-1">Revision</th>
                  {/* <th className="border p-1">Specs/IFC Drg.NO</th> */}
                  <th className="border p-1">Description</th>
                </tr>
              </thead>
              <tbody>
                {formData.documents.length > 0 ? (
                  formData.documents.map((doc, index) => (
                    <tr key={index}>
                      <td className="border p-1 text-center">{index + 1}</td>
                      <td className="border p-1">
                        <input
                          type="text"
                          className="w-full h-5 text-xs"
                          value={doc.docRef || ""}
                          disabled
                        />
                      </td>
                      <td className="border p-1">
                        <input
                          type="text"
                          className="w-full h-5 text-xs"
                          value={doc.revision || ""}
                          disabled
                        />
                      </td>
                      {/* <td className="border p-1">
                        <input
                          type="text"
                          className="w-full h-5 text-xs"
                          value={doc.specNo || ""}
                          disabled
                        />
                      </td> */}
                      <td className="border p-1">
                        <input
                          type="text"
                          className="w-full h-5 text-xs"
                          value={doc.description || ""}
                          disabled
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="border p-1 text-center">
                      No documents available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="text-xs mb-2">
              <div className="font-semibold mb-1">Submitted For:</div>
              <div className="grid grid-cols-4 gap-1">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={formData.ForApproval || false}
                    disabled
                  />
                  <span>For Approval</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={formData.ForYourInformation || false}
                    disabled
                  />
                  <span>For Your Information</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={formData.ResponseRequired || false}
                    disabled
                  />
                  <span>Response Required</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.Approved} disabled />
                  <span>Approved</span>
                </label>

                <label className="flex items-center">
                  <input type="checkbox" checked={formData.ApprovedWithComments} disabled />
                  <span>Approved with Comments</span>
                </label>

                <label className="flex items-center">
                  <input type="checkbox" checked={formData.ReviseAndResubmit} disabled />
                  <span>Revise & Resubmit</span>
                </label>

                <label className="flex items-center">
                  <input type="checkbox" checked={formData.Rejected} disabled />
                  <span>Rejected</span>
                </label>

                <label className="flex items-center">
                  <input type="checkbox" checked={formData.Responded} disabled />
                  <span>Responded</span>
                </label>
                {formData.ResponseRequired && (
                  <div className="flex items-center">
                    <label className="mr-2 font-semibold">Response Date:</label>
                    <input
                      type="date"
                      className="w-32 h-6 text-xs"
                      value={formData.responseDate ? normalizeDate(formData.responseDate) : ""}
                      disabled
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 text-xs">
              {!hideContractorRemarks && (
                <div className="border p-2">
                  <div className="font-semibold mb-1">Contractor remarks</div>
                  <textarea
                    className="w-full border h-12 mb-1 text-xs p-1"
                    value={formData.contractorText}
                    disabled
                  ></textarea>
                </div>
              )}

              {!hideEngineerRemarks && (
                <div className="border p-2">
                  <div className="font-semibold mb-1">Engineer's Representative</div>
                  <textarea
                    className="w-full border h-12 mb-1 text-xs p-1"
                    value={formData.engineerText}
                    disabled
                  ></textarea>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block">Engineer Name:</label>
                      <input
                        type="text"
                        className="border w-full h-6"
                        value={formData.engineerName}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block">Engineer Signature:</label>
                      <canvas id="signaturePad2" className="border rounded w-full h-6 bg-white"></canvas>
                    </div>
                    <div>
                      <label className="block">Engineer Date:</label>
                      <input
                        type="date"
                        className="border w-full h-6"
                        value={formData.engineerDate ? formData.engineerDate.split("T")[0] : ""}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              )}

              {!hideClientRemarks && (
                <div className="border p-2">
                  <div className="font-semibold mb-1">Client's Representative</div>
                  <textarea
                    className="w-full border h-12 mb-1 text-xs p-1"
                    value={formData.clientText}
                    disabled
                  ></textarea>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block">Client Name:</label>
                      <input
                        type="text"
                        className="border w-full h-6"
                        value={formData.clientName}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block">Client Signature:</label>
                      <canvas id="signaturePad3" className="border rounded w-full h-6 bg-white"></canvas>
                    </div>
                    <div>
                      <label className="block">Client Date:</label>
                      <input
                        type="date"
                        className="border w-full h-6"
                        value={formData.clientDate ? formData.clientDate.split("T")[0] : ""}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="border p-2 text-center">
                <h5 className="text-sm font-semibold">
                  No comments or signature on this form relieve the contractor from any obligations under the contract
                </h5>
              </div>

              <div className="border p-2 mt-4 flex">
                <div className="w-2/5 flex flex-col space-y-2">
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={formData.Original || false}
                      disabled
                    />
                    Original
                  </label>
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={formData.Copies || false}
                      disabled
                    />
                    Copies
                  </label>
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={formData.Contractor || false}
                      disabled
                    />
                    Contractor
                  </label>
                </div>
                <div className="w-3/5 flex items-center" style={{ marginLeft: '10.5rem' }}>
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold">Approved By:</label>
                      <input
                        type="text"
                        className="border w-44 h-10 p-2 text-sm"
                        value={formData.approvedBy}
                        disabled
                      />
                      <label className="text-xs font-semibold mt-1">Date:</label>
                      <input
                        type="date"
                        className="border w-44 h-10 p-2 text-sm"
                        value={formData.approvedDate ? formData.approvedDate.split("T")[0] : ""}
                        disabled
                      />
                    </div>
                    <img src={approveSeal} alt="Approved Seal" className="w-32 h-32 opacity-90" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              <Button onClick={handlePrint} className="bg-blue-500 text-white px-3 py-1 text-sm rounded">
                Print Form
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ViewTransmittal;